import { useState } from "react";
import useSWR from "swr";
import { Table, Button, Group, Stack, Text, Select, Modal, Textarea, Alert, ActionIcon, Tooltip } from "@mantine/core";
import { Check, X, Pencil } from "lucide-react";
import { StudentFormFields, StudentFormData, toStudentFormData, fromStudentFormData } from "../../../shared/components/StudentFormFields";
import { Contacts } from "../../../shared/components/Contacts";
import { Pagination } from "../../../shared/components/Pagination";
import { useFetch, getRegistrations, approveRegistration, declineRegistration, updateRegistration, Registration, RegistrationStatus, REGISTRATION_STATUSES } from "../../../shared/api";
import { ErrorDisplay, Loading, formatDateTime, formatFullName } from "../../../shared/utils";
import { FilterBar, useFilterDraft } from "../../../shared/components/FilterBar";

const PAGE_SIZE = 50;
const STATUS_LABEL: Record<RegistrationStatus, string> = { pending: "Ожидают", approved: "Одобрены", declined: "Отклонены" };

export function RegistrationsTab() {
  const { fetch } = useFetch();
  const [applied, setApplied] = useState<{ status: RegistrationStatus }>({ status: "pending" });
  const { draft, patch, dirty } = useFilterDraft(applied);
  const [page, setPage] = useState(1);
  const apply = (f: typeof applied) => { setApplied(f); setPage(1); };
  const { status } = applied;

  const { data, error, isLoading, mutate } = useSWR(["admin/registrations", status, page], () => getRegistrations({ status, limit: PAGE_SIZE, start: (page - 1) * PAGE_SIZE, fetch }));
  const [busy, setBusy] = useState<{ id: string; action: "approve" | "decline" } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [declineTarget, setDeclineTarget] = useState<Registration | null>(null);
  const [declineReason, setDeclineReason] = useState("");
  const [editTarget, setEditTarget] = useState<Registration | null>(null);
  const [editForm, setEditForm] = useState<StudentFormData | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  async function approve(r: Registration) {
    setActionError(null); setBusy({ id: r.id, action: "approve" });
    try {
      try { await approveRegistration({ id: r.id, fetch }); }
      catch (e: any) {
        if (String(e?.message).startsWith("409") && window.confirm(`Студент с почтой ${r.phystech_email} уже существует. Перезаписать?`)) {
          await approveRegistration({ id: r.id, overwrite: true, fetch });
        } else throw e;
      }
      await mutate();
    } catch (e: any) { setActionError(e?.message ?? String(e)); }
    finally { setBusy(null); }
  }

  async function confirmDecline() {
    if (!declineTarget) return;
    setBusy({ id: declineTarget.id, action: "decline" });
    try {
      await declineRegistration({ id: declineTarget.id, reason: declineReason.trim() || undefined, fetch });
      setDeclineTarget(null); setDeclineReason(""); await mutate();
    } catch (e: any) { setActionError(e?.message ?? String(e)); }
    finally { setBusy(null); }
  }

  async function saveEdit() {
    if (!editTarget || !editForm) return;
    setSaving(true); setEditError(null);
    try {
      await updateRegistration({ id: editTarget.id, patch: { ...fromStudentFormData(editForm), phystech_email: editForm.phystech_email.trim() }, fetch });
      setEditTarget(null); setEditForm(null); await mutate();
    } catch (e: any) { setEditError(e?.message ?? String(e)); }
    finally { setSaving(false); }
  }

  if (error) return <ErrorDisplay error={error} />;

  return (
    <Stack>
      <FilterBar dirty={dirty} onApply={() => apply(draft)} onReset={() => apply({ status: "pending" })}>
        <Select w={220} label="Статус" data={REGISTRATION_STATUSES.map(s => ({ value: s, label: STATUS_LABEL[s] }))} value={draft.status} onChange={v => v && patch({ status: v as RegistrationStatus })} allowDeselect={false} />
      </FilterBar>
      {actionError && <Alert color="red">{actionError}</Alert>}
      {isLoading || !data ? <Loading /> : (
        <>
          <Table highlightOnHover>
            <Table.Thead><Table.Tr><Table.Th w={240}>ФИО</Table.Th><Table.Th>Контакты</Table.Th><Table.Th w={180}>Подана</Table.Th><Table.Th w={220}>{status === "pending" ? "Действия" : "Кто обработал"}</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {data.map(r => (
                <Table.Tr key={r.id}>
                  <Table.Td>{formatFullName(r, true)}</Table.Td>
                  <Table.Td><Contacts c={r} /></Table.Td>
                  <Table.Td><Text fz="sm">{formatDateTime(r.created_at)}</Text></Table.Td>
                  <Table.Td>
                    {status === "pending" ? (
                      <Group gap="xs" wrap="nowrap">
                        <Tooltip label="Одобрить"><ActionIcon variant="filled" color="green" loading={busy?.id === r.id && busy.action === "approve"} onClick={() => approve(r)}><Check size={16} /></ActionIcon></Tooltip>
                        <Tooltip label="Отклонить"><ActionIcon variant="filled" color="red" onClick={() => { setDeclineTarget(r); setDeclineReason(""); }}><X size={16} /></ActionIcon></Tooltip>
                        <Tooltip label="Редактировать"><ActionIcon variant="subtle" color="gray" onClick={() => { setEditTarget(r); setEditForm(toStudentFormData(r)); setEditError(null); }}><Pencil size={16} /></ActionIcon></Tooltip>
                      </Group>
                    ) : (
                      <Stack gap={2}>
                        {r.reviewed_by && <Text fz="sm">{r.reviewed_by}</Text>}
                        {r.reviewed_at && <Text fz="xs" c="dimmed">{formatDateTime(r.reviewed_at)}</Text>}
                        {r.decline_reason && <Text fz="xs" c="dimmed">причина: {r.decline_reason}</Text>}
                      </Stack>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {data.length === 0 && <Table.Tr><Table.Td colSpan={4}><Text c="dimmed" ta="center" py="md">Заявок нет</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
          </Table>
          <Pagination page={page} shown={data.length} hasNext={data.length >= PAGE_SIZE} onChange={setPage} />
        </>
      )}

      <Modal opened={declineTarget !== null} onClose={() => setDeclineTarget(null)} title="Отклонить заявку">
        <Stack>
          {declineTarget && <Text fz="sm">{formatFullName(declineTarget, true)} — {declineTarget.phystech_email}</Text>}
          <Textarea label="Причина" autosize minRows={2} value={declineReason} onChange={e => setDeclineReason(e.target.value)} />
          <Group justify="flex-end"><Button variant="default" onClick={() => setDeclineTarget(null)}>Отмена</Button><Button color="red" loading={busy?.id === declineTarget?.id} onClick={confirmDecline}>Отклонить</Button></Group>
        </Stack>
      </Modal>

      <Modal opened={editTarget !== null} onClose={() => setEditTarget(null)} title="Редактировать заявку">
        {editForm && (
          <Stack>
            {editError && <Alert color="red">{editError}</Alert>}
            <StudentFormFields form={editForm} onChange={u => setEditForm(f => f ? u(f) : null)} />
            <Group justify="flex-end"><Button variant="default" onClick={() => setEditTarget(null)}>Отмена</Button><Button loading={saving} disabled={!editForm.last_name.trim() || !editForm.first_name.trim() || !editForm.phystech_email.trim()} onClick={saveEdit}>Сохранить</Button></Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
